import type { ChangeEvent } from 'react'
import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Dialog } from '@gto/ui/dialog'
import { Field } from '@gto/ui/field'
import { GroupedField } from '@gto/ui/grouped-field'
import { Grow } from '@gto/ui/grow'
import { Paper } from '@gto/ui/paper'
import { Radio, RadioGroup } from '@gto/ui/radio'
import { Select } from '@gto/ui/select'
import { Typography } from '@gto/ui/typography'

import {
  useDealPersonalizedDealsRetrieve,
  useDealPokerSitesRetrieve,
  useRoomReportableSitesList,
  useRoomReportSignupCreate,
} from '@/api/gen'
import { TextInput } from '@/components/text-input'
import { useFormValidation } from '@/hooks/use-form-validation'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { ReportUsernameFormData } from '@/utils/validation'
import { reportUsernameSchema } from '@/utils/validation'

import { ReportFromDealSteps } from './report-from-deal-steps'

type ReportStatus = {
  type: 'success' | 'error'
  message?: string
}

export type ReportUsernameModalProps = {
  open: boolean
  onClose: () => void
  fromDeal?: boolean
  dealId?: string
  room?: string
}

export const ReportUsernameModal = ({
  open,
  onClose,
  fromDeal = false,
  dealId,
  room,
}: ReportUsernameModalProps) => {
  const { formatMessage } = useIntl()
  const { validate, errors, clearFieldError, isValid } = useFormValidation(reportUsernameSchema)
  const [reportStatus, setReportStatus] = useState<ReportStatus | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const { mutate: createReport, isPending } = useRoomReportSignupCreate({
    mutation: {
      onSuccess: () => {
        setReportStatus({
          type: 'success',
          message: formatMessage({
            id:
              formValues.creation_type === undefined
                ? 'reportUsername.success'
                : `reportUsername.success.${formValues.creation_type}`,
          }),
        })
      },
      onError: (error: AxiosError) => {
        setReportStatus({
          type: 'error',
          message: getAxiosErrorMessage(error) || formatMessage({ id: 'reportUsername.error' }),
        })

        setTimeout(() => {
          scrollToBottom()
        }, 100)
      },
    },
  })
  const { data: roomDeals } = useDealPokerSitesRetrieve(room ?? '', {
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      enabled: room !== undefined,
    },
  })

  const { data: listOfDeals } = useDealPersonalizedDealsRetrieve({
    query: {
      enabled: room === undefined,
    },
  })

  const { data: sites = [] } = useRoomReportableSitesList({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
    },
  })

  const data = roomDeals ?? listOfDeals ?? { deals: [] }
  const [formValues, setFormValues] = useState<ReportUsernameFormData>({
    deal: dealId ?? '',
    room_username: '',
    room_email: '',
    creation_type: undefined,
    poker_site: '',
  })
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, name },
    } = event
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value }
      validate(updated)

      return updated
    })
  }

  const onSubmit = () => {
    setReportStatus(null)

    const isValid = validate(formValues)
    if (isValid) {
      const fields = {
        room_username: formValues.room_username,
        room_email: formValues.room_email,
        creation_type: formValues.creation_type,
        ...(formValues.creation_type === 'NEW' && { deal: formValues.deal }),
        ...(formValues.creation_type === 'OLD' && { poker_site: formValues.poker_site }),
      }

      createReport({
        data: fields,
      })
    }
  }

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }

  const handleClose = () => {
    onClose()
    setFormValues({
      deal: '',
      room_username: '',
      room_email: '',
      creation_type: undefined,
      poker_site: '',
    })
    setReportStatus(null)
  }

  const getModalTitle = () => {
    if (reportStatus?.type === 'success') {
      return formatMessage({
        id: 'common.labels.thankYou',
      })
    }

    return fromDeal
      ? formatMessage({ id: 'reportUsername.fromDeal.title' })
      : formatMessage({ id: 'reportUsername.title' })
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-[500px] sm:top-[60px] sm:translate-y-0">
          <Dialog.Header>{getModalTitle()}</Dialog.Header>

          <div
            ref={contentRef}
            style={{ scrollBehavior: 'smooth' }}
          >
            <div className="flex flex-col gap-8">
              {reportStatus?.type === 'success' ? (
                <>
                  {formValues.creation_type === 'OLD' && (
                    <Alert
                      className="-mb-4"
                      heading={<FormattedMessage id="reportUsername.success.OLD.warning.title" />}
                      severity="warning"
                      message={
                        <FormattedMessage id="reportUsername.success.OLD.warning.description" />
                      }
                    />
                  )}

                  <Alert
                    message={reportStatus.message}
                    severity="success"
                    heading={
                      formValues.creation_type === 'OLD' && (
                        <FormattedMessage id="reportUsername.success.OLD.title" />
                      )
                    }
                  />
                </>
              ) : (
                <>
                  {fromDeal && (
                    <p>
                      <FormattedMessage
                        id="reportUsername.fromDeal.description"
                        values={{
                          highlight: (chunks) => (
                            <Typography
                              className="font-semibold"
                              color="primary"
                              component="span"
                            >
                              {chunks}
                            </Typography>
                          ),
                        }}
                      />
                    </p>
                  )}
                  <div className="flex flex-col gap-4">
                    {fromDeal && <ReportFromDealSteps />}

                    <Paper
                      className="rounded-md p-4"
                      elevation="1"
                    >
                      <GroupedField.Root invalid={Boolean(errors.creation_type)}>
                        <GroupedField.Legend>
                          <FormattedMessage id="reportUsername.isOldOrNewAccount" />
                        </GroupedField.Legend>
                        <GroupedField.Controls
                          orientation="vertical"
                          aria-describedby={
                            errors.creation_type === undefined ? undefined : 'creation-type-error'
                          }
                        >
                          <RadioGroup
                            name="creation_type"
                            value={formValues.creation_type}
                            onValueChange={(value: string) => {
                              const creationType =
                                value === 'NEW' || value === 'OLD' ? value : undefined

                              setFormValues((prev) => ({
                                ...prev,
                                creation_type: creationType,
                              }))

                              clearFieldError('creation_type')
                              setTimeout(() => {
                                scrollToBottom()
                              }, 0)
                            }}
                          >
                            <Radio
                              label={<FormattedMessage id="reportUsername.oldNewAcc.new" />}
                              value="NEW"
                            />
                            <Radio
                              label={<FormattedMessage id="reportUsername.oldNewAcc.old" />}
                              value="OLD"
                            />
                          </RadioGroup>
                        </GroupedField.Controls>
                        {errors.creation_type !== undefined && (
                          <GroupedField.Error id="creation-type-error">
                            {errors.creation_type}
                          </GroupedField.Error>
                        )}
                      </GroupedField.Root>
                    </Paper>

                    <Grow
                      unmountOnExit
                      in={Boolean(formValues.creation_type)}
                    >
                      <div>
                        {formValues.creation_type === 'NEW' ? (
                          <Field.Root invalid={Boolean(errors.deal)}>
                            <Field.Label>
                              <FormattedMessage id="common.labels.dealTitle" />
                            </Field.Label>
                            <Select
                              value={formValues.deal ?? ''}
                              onValueChange={(value) => {
                                setFormValues((prev) => ({ ...prev, deal: value }))
                                clearFieldError('deal')
                              }}
                            >
                              <Select.Trigger aria-invalid={Boolean(errors.deal)}>
                                <Select.Value>
                                  {(value) =>
                                    typeof value === 'string' && value.length > 0 ? (
                                      data.deals.find((d) => d.deal_id === value)?.deal_name
                                    ) : (
                                      <FormattedMessage id="common.labels.selectADeal" />
                                    )
                                  }
                                </Select.Value>
                                <Select.Icon />
                              </Select.Trigger>
                              <Select.Dropdown>
                                <Select.List>
                                  {data.deals.length > 0 ? (
                                    data.deals.map((deal) => (
                                      <Select.Item
                                        key={deal.deal_id}
                                        value={deal.deal_id}
                                      >
                                        {deal.deal_name}
                                      </Select.Item>
                                    ))
                                  ) : (
                                    <Select.Item value="">
                                      <FormattedMessage id="common.labels.noDeals" />
                                    </Select.Item>
                                  )}
                                </Select.List>
                              </Select.Dropdown>
                            </Select>
                            {errors.deal !== undefined && (
                              <Field.Error
                                match
                                role="alert"
                              >
                                {errors.deal}
                              </Field.Error>
                            )}
                          </Field.Root>
                        ) : (
                          <Field.Root invalid={Boolean(errors.poker_site)}>
                            <Field.Label>
                              <FormattedMessage id="common.labels.roomTitle" />
                            </Field.Label>
                            <Select
                              value={formValues.poker_site ?? ''}
                              onValueChange={(value) => {
                                setFormValues((prev) => ({ ...prev, poker_site: value }))
                                clearFieldError('poker_site')
                              }}
                            >
                              <Select.Trigger aria-invalid={Boolean(errors.poker_site)}>
                                <Select.Value>
                                  {(value) =>
                                    typeof value === 'string' && value.length > 0 ? (
                                      sites.find((site) => site.slug === value)?.name
                                    ) : (
                                      <FormattedMessage id="common.labels.selectAPokerRoom" />
                                    )
                                  }
                                </Select.Value>
                                <Select.Icon />
                              </Select.Trigger>
                              <Select.Dropdown>
                                <Select.List>
                                  {sites.length > 0 ? (
                                    sites.map((site) => (
                                      <Select.Item
                                        key={site.slug}
                                        value={site.slug}
                                      >
                                        {site.name}
                                      </Select.Item>
                                    ))
                                  ) : (
                                    <Select.Item value="">
                                      <FormattedMessage id="common.labels.noSites" />
                                    </Select.Item>
                                  )}
                                </Select.List>
                              </Select.Dropdown>
                            </Select>
                            {errors.poker_site !== undefined && (
                              <Field.Error
                                match
                                role="alert"
                              >
                                {errors.poker_site}
                              </Field.Error>
                            )}
                          </Field.Root>
                        )}
                        <div className="flex flex-col gap-4">
                          <TextInput
                            error={errors.room_username}
                            label="common.labels.username"
                            input={{
                              size: 'medium',
                              onChange,
                              value: formValues.room_username,
                              name: 'room_username',
                            }}
                          />
                          <TextInput
                            error={errors.room_email}
                            label="common.labels.email"
                            input={{
                              size: 'medium',
                              onChange,
                              value: formValues.room_email,
                              name: 'room_email',
                              type: 'email',
                            }}
                          />
                        </div>
                      </div>
                    </Grow>

                    {reportStatus?.type === 'error' && (
                      <Alert
                        heading={reportStatus.message}
                        severity="warning"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <Dialog.Footer>
            {reportStatus?.type === 'success' ? (
              <Button
                color="primary"
                size="large"
                variant="contained"
                onClick={handleClose}
              >
                <FormattedMessage id="common.controls.close" />
              </Button>
            ) : (
              <>
                <Button
                  color="secondary"
                  disabled={isPending}
                  size="large"
                  variant="contained"
                  onClick={handleClose}
                >
                  <FormattedMessage id="common.controls.cancel" />
                </Button>
                <Button
                  color="primary"
                  disabled={!isValid || isPending || !formValues.creation_type}
                  loading={isPending}
                  size="large"
                  variant="contained"
                  onClick={onSubmit}
                >
                  <FormattedMessage
                    id={fromDeal ? 'common.controls.reportUsername' : 'common.controls.submit'}
                  />
                </Button>
              </>
            )}
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
